import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import ShortcutOutlinedIcon from "@mui/icons-material/ShortcutOutlined";
import { Box } from "@mui/material";
import React, { useMemo } from "react";
import {
  Folder,
} from "@mui/icons-material";

export const getFileIconAndColor = ({
  type,
  extension = "",
  isShortcut = false,
  overrideColor = null,
  fontSize = 25,
  mode = "dark",
}) => {
  // Define file type icons and colors
  const fileTypes = {
    pdf: { icon: <PictureAsPdfOutlinedIcon />, color: "red" },
    jpg: { icon: <ImageOutlinedIcon />, color: "rgba(227, 100, 108, 1)" },
    jpeg: { icon: <ImageOutlinedIcon />, color: "rgba(227, 100, 108, 1)" },
    png: { icon: <ImageOutlinedIcon />, color: "rgba(227, 100, 108, 1)" },
    doc: { icon: <DescriptionOutlinedIcon />, color: "rgba(55, 103, 177, 1)" },
    docx: { icon: <DescriptionOutlinedIcon />, color: "rgba(55, 103, 177, 1)" },
    xlsx: { icon: <DescriptionOutlinedIcon />, color: "rgba(44, 183, 113, 1)" },
    xls: { icon: <DescriptionOutlinedIcon />, color: "rgba(44, 183, 113, 1)" },
    csv: { icon: <DescriptionOutlinedIcon />, color: "rgba(44, 183, 113, 1)" },
    mp4: { icon: <VideoLibraryOutlinedIcon />, color: "purple" },
    avi: { icon: <VideoLibraryOutlinedIcon />, color: "purple" },
  };

  let icon, color;

  if (type === "folder") {
    // Folder Icons
    icon = <Folder />;
    color = overrideColor?.startsWith('#') ? overrideColor : "rgba(255, 193, 7, 1)"; // Yellow folder unless overridden
  } else {
    // File Icons
    ({ icon, color } = fileTypes[extension] || {
      icon: <InsertDriveFileOutlinedIcon />,
      color: overrideColor?.startsWith('#') ? overrideColor : "gray",
    });
  }

  return {
    icon: isShortcut ? (
      <Box position="relative" display="inline-block">
        {React.cloneElement(icon, { style: { fontSize: fontSize, color } })}
        <ShortcutOutlinedIcon
          sx={{
            fontSize: 12,
            position: "absolute",
            bottom: 0,
            right: 0,
            // backgroundColor: "#191919",
            background: mode === 'dark' ? "rgba(31, 31, 31, 1)" : "rgb(240, 240, 240)",

            borderRadius: "50%",
          }}
        />
      </Box>
    ) : (
      React.cloneElement(icon, { style: { fontSize: fontSize, color } })
    ),
    color,
  };
};

// export default getFileIconAndColor;

export const categorizeItems = (items) => {
  // const now = new Date();
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  weekAgo.setHours(0, 0, 0, 0);

  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  lastMonth.setHours(0, 0, 0, 0);

  const categories = {
    "Today": [],
    "Earlier this week": [],
    "Last month": [],
  };

  items.forEach((item) => {
    // Get the timestamp from the last activity
    const lastActivity = item.activities && item.activities.length > 0
      ? item.activities[item.activities.length - 1]
      : null;

    if (!lastActivity) return; // Skip if no activities

    const activityDate = new Date(lastActivity.timestamp);
    activityDate.setHours(0, 0, 0, 0);

    if (activityDate >= todayStart) {
      categories["Today"].push(item);
    } else if (activityDate >= weekAgo) {
      categories["Earlier this week"].push(item);
    } else if (activityDate >= lastMonth) {
      categories["Last month"].push(item);
    }
  });

  return categories;
};

export const useSortedItems = (items, selectedSort, isDescending, ownerId) => {
  return useMemo(() => {
    if (!items) return [];

    // Create a copy of the array to avoid mutating the original
    const itemsToSort = [...items];

    switch (selectedSort) {
      case "Name":
        itemsToSort.sort((a, b) =>
          isDescending
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name)
        );
        break;

      case "Last Modified":
        itemsToSort.sort((a, b) => {
          const dateA = new Date(a.updatedAt || a.createdAt);
          const dateB = new Date(b.updatedAt || b.createdAt);
          return isDescending ? dateB - dateA : dateA - dateB;
        });
        break;

      case "Last Modified by Me":
        // Filter items that have been modified by the current user
        // Then sort by modification time
        const modifiedByMe = itemsToSort.filter(item =>
          item.activities?.some(activity =>
            activity.by === ownerId &&
            activity.name === "Modified"
          )
        );

        modifiedByMe.sort((a, b) => {
          const lastModA = a.activities
            .filter(act => act.name === "Modified" && act.by === ownerId)
            .reduce((latest, current) =>
              new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
            );

          const lastModB = b.activities
            .filter(act => act.name === "Modified" && act.by === ownerId)
            .reduce((latest, current) =>
              new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
            );

          return isDescending
            ? new Date(lastModB.timestamp) - new Date(lastModA.timestamp)
            : new Date(lastModA.timestamp) - new Date(lastModB.timestamp);
        });

        // Combine with items not modified by user
        const notModifiedByMe = itemsToSort.filter(item =>
          !item.activities?.some(activity =>
            activity.by === ownerId &&
            activity.name === "Modified"
          )
        );

        return [...modifiedByMe, ...notModifiedByMe];

      case "Last Opened by Me":
        // Filter items that have been accessed by the current user
        // Then sort by access time
        const accessedByMe = itemsToSort.filter(item =>
          item.activities?.some(activity =>
            activity.by === ownerId &&
            activity.name === "Accessed"
          )
        );

        accessedByMe.sort((a, b) => {
          const lastAccessA = a.activities
            .filter(act => act.name === "Accessed" && act.by === ownerId)
            .reduce((latest, current) =>
              new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
            );

          const lastAccessB = b.activities
            .filter(act => act.name === "Accessed" && act.by === ownerId)
            .reduce((latest, current) =>
              new Date(current.timestamp) > new Date(latest.timestamp) ? current : latest
            );

          return isDescending
            ? new Date(lastAccessB.timestamp) - new Date(lastAccessA.timestamp)
            : new Date(lastAccessA.timestamp) - new Date(lastAccessB.timestamp);
        });

        // Combine with items not accessed by user
        const notAccessedByMe = itemsToSort.filter(item =>
          !item.activities?.some(activity =>
            activity.by === ownerId &&
            activity.name === "Accessed"
          )
        );

        return [...accessedByMe, ...notAccessedByMe];

      default:
        return itemsToSort;
    }

    return itemsToSort;
  }, [items, selectedSort, isDescending, ownerId]);
};

export const fileTypeMapping = {
  Document: ["pdf", "doc", "docx", "txt", "xls", "xlsx", "ppt", "pptx"],
  Image: ["jpg", "jpeg", "png", "gif", "bmp", "svg"],
  Video: ["mp4", "avi", "mkv", "mov", "wmv"],
  Audio: ["mp3", "wav", "aac", "ogg", "flac"]
};

export const formatSize = (size) => {
  if (!size || size <= 0) return '0 bytes';
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  const unitIndex = Math.floor(Math.log(size) / Math.log(1024));
  return `${(size / Math.pow(1024, unitIndex)).toFixed(0)} ${units[unitIndex]}`;
};